import type { NostrEvent } from '@nostrify/nostrify';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getArticleMeta } from '@/hooks/useArticles';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Calendar } from 'lucide-react';

interface ArticleCardProps {
  event: NostrEvent;
}

export function ArticleCard({ event }: ArticleCardProps) {
  const meta = getArticleMeta(event);
  const author = useAuthor(event.pubkey);
  const authorMeta = author.data?.metadata;
  const displayName = authorMeta?.name ?? genUserName(event.pubkey);

  const naddr = nip19.naddrEncode({
    kind: 30023,
    pubkey: event.pubkey,
    identifier: meta.dTag,
  });

  const formattedDate = meta.publishedAt
    ? new Date(meta.publishedAt * 1000).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <Link to={`/${naddr}`} className="group block">
      <Card className="overflow-hidden border-0 shadow-none hover:bg-accent/50 transition-colors duration-200 py-0">
        <CardContent className="p-0">
          <div className="flex gap-5 p-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2.5">
                <Avatar size="sm">
                  {authorMeta?.picture && (
                    <AvatarImage src={authorMeta.picture} alt={displayName} />
                  )}
                  <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-muted-foreground truncate">
                  {displayName}
                </span>
              </div>

              <h2 className="text-lg md:text-xl font-bold font-serif leading-tight mb-1.5 group-hover:text-primary transition-colors line-clamp-2">
                {meta.title}
              </h2>

              {meta.summary && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {meta.summary}
                </p>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                {formattedDate && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="size-3" />
                    {formattedDate}
                  </span>
                )}
                {meta.hashtags.slice(0, 3).map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {meta.image && (
              <div className="hidden sm:block shrink-0 w-32 h-24 rounded-lg overflow-hidden">
                <img
                  src={meta.image}
                  alt={meta.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
